<template>
    <div class="max-w-3xl mx-auto px-4 py-4 font-sans text-gray-800">

        <div class="mb-8">
            <NuxtLink to="/resources" class="text-secondary hover:text-secondary/80 flex items-center mb-4">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
                Resources
            </NuxtLink>
        </div>

        <img src="/images/resources/spreadsheet-vs-pdf-inventory.png" alt="Spreadsheet vs Professional Inventory"
            class="w-full h-64 object-cover rounded-xl shadow-md mb-8">

        <h1 class="text-4xl font-extrabold tracking-tight text-gray-900 mb-8 leading-tight">
            Why a Spreadsheet Isn’t Enough: Creating Audit-Ready Evidence for Your Home Insurance
        </h1>

        <p class="text-lg leading-relaxed mb-6">Many proactive homeowners believe they are prepared because they have a
            "Master List" saved in Excel or a
            shoebox full of fading receipts. It is a logical first step, but in the wake of a total loss—where tens of
            thousands of dollars are on the line—a simple spreadsheet is often the weakest form of evidence you can
            provide.</p>

        <p class="text-lg leading-relaxed mb-6">When you file a major claim, you are entering a forensic environment. An
            insurance adjuster isn't looking for a
            "list"; they are looking for verifiable proof.</p>

        <p class="text-lg leading-relaxed mb-6">If your documentation doesn’t meet the evidentiary standards of the
            insurance industry, your spreadsheet is
            little more than a wish list. Here is why professional, audit-ready formatting is the difference between a
            lowball settlement and a full recovery.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">The "Hearsay" Problem of Simple Lists</h2>
        <p class="text-lg leading-relaxed mb-6">A spreadsheet is a self-authored document. From an adjuster's
            perspective, it is "hearsay"—a collection of
            claims that have not been verified.</p>

        <p class="text-lg leading-relaxed mb-6">If you list a "75-inch OLED TV" on a spreadsheet, the adjuster has no
            way to verify the brand, the condition, or
            the specific features that drive its value. Without a photo tied directly to that line item, the adjuster is
            often required by their company’s software to default to the "standard" or "entry-level" model of a 75-inch
            TV.</p>

        <p class="text-lg leading-relaxed mb-6">On a large claim, these "minor" downgrades across hundreds of items can
            result in a settlement gap of $20,000 to
            $50,000.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">Forensic Photography: The Weight of Visual Proof</h2>
        <p class="text-lg leading-relaxed mb-6">Professional documentation relies on the "Forensic Pair" method: a
            wide-angle shot of the room to establish the
            item's location, and a high-resolution macro shot of the item’s serial number and model plate.</p>

        <p class="text-lg leading-relaxed mb-6">A spreadsheet cannot easily house these images in a way that is useful
            to an adjuster. A box of receipts is even
            worse; thermal paper fades over time, and a receipt for a purchase five years ago doesn’t prove you still
            owned
            the item at the time of the loss.</p>

        <p class="text-lg leading-relaxed mb-6">Audit-ready evidence combines the data (make, model, price) with the
            visual proof (photos of the item in your
            home) into a single, immutable record. When an adjuster sees a professional PDF report where every claim is
            backed by a timestamped photo, their ability to "negotiate down" vanishes. You have provided a forensic
            fact,
            not a memory.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">The Psychology of Professional Formatting</h2>
        <p class="text-lg leading-relaxed mb-6">Insurance adjusters are human. They deal with hundreds of disorganized,
            emotional, and under-prepared homeowners
            every year.</p>

        <p class="text-lg leading-relaxed mb-6">When you hand an adjuster a messy stack of papers or a link to a raw
            spreadsheet, you are signaling that your
            documentation is likely incomplete. It invites them to scrutinize every line.</p>

        <p class="text-lg leading-relaxed mb-6">Conversely, when you present a professional PDF inventory
            report—complete with a table of contents, categorized assets, clear photo appendices, and model numbers—you
            are signaling that you are an "Audit-Ready" policyholder. This professional friction immediately changes the
            tone of the negotiation. It shows that you have done the work, you have the proof, and you are prepared to
            escalate the claim if the settlement isn't fair.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">The Pivot: Why "Cloud-Based" Tools are a Privacy Trap
        </h2>
        <p class="text-lg leading-relaxed mb-6">While many homeowners realize a spreadsheet is insufficient, they often
            turn to cloud-based inventory apps as a
            solution. This creates a new, more dangerous problem.</p>

        <p class="text-lg leading-relaxed mb-6">To use these apps, you must upload a digital map of your
            life—including high-value jewelry, electronics, and
            home layout—to a company’s server. You are trading your privacy for a PDF. If that company is hacked, or if
            they
            sell your data to "marketing partners," your home's most intimate details are compromised.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">The ClaimReady Standard: Professional Proof, Total
            Privacy</h2>
        <p class="text-lg leading-relaxed mb-6">We designed ClaimReady to bridge the gap between "DIY spreadsheets" and
            "Security-risk cloud apps."</p>

        <p class="text-lg leading-relaxed mb-6">ClaimReady is a Zero-Knowledge utility. It helps you build a
            professional, forensic-grade PDF report in under 20
            minutes, but it does so locally. Your photos and data never touch our servers. They stay in your browser, on
            your device, until the final, audit-ready report is generated.</p>

        <p class="text-lg leading-relaxed mb-6">You get the professional formatting that adjusters respect, without the
            "memory test" of a spreadsheet or the
            privacy nightmare of the cloud.</p>

        <UCard class="font-semibold text-lg">Stop relying on hearsay. Build a forensic record of your wealth in 20
            minutes.</UCard>

        <div class="mt-8 flex justify-center">
            <UButton label="Get Started" to="/" size="xl" />
        </div>
    </div>
</template>

<script setup lang="ts">

definePageMeta({
    layout: 'default'
})

useHead({
    title: `Why a Spreadsheet Isn't Enough for Home Insurance | ClaimReady`,
    meta: [
        { name: 'description', content: `A basic list or spreadsheet won't stand up to an insurance adjuster. Learn why forensic, audit-ready PDF documentation is essential for a maximum payout.` },
        { name: 'keywords', content: 'home insurance claim tips, documenting valuables for insurance, insurance adjuster secrets, audit-ready inventory, zero-knowledge apps' },
        // Open Graph
        { property: 'og:title', content: `Why a Spreadsheet Isn't Enough: Creating Audit-Ready Evidence` },
        { property: 'og:description', content: `Don't let a "memory test" cost you thousands. Move beyond spreadsheets and create forensic proof that adjusters can't dispute.` },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: 'https://getclaimready.app/resources/why-spreadsheet-isnt-enough-for-insurance-claims' },
        { property: 'og:image', content: 'https://getclaimready.app/images/resources/spreadsheet-vs-pdf-inventory.png' },
        // Twitter
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: 'Spreadsheets vs. Forensic Evidence' },
        { name: 'twitter:description', content: 'Your spreadsheet is just hearsay. Give your insurance adjuster audit-ready proof without sacrificing your privacy.' },
        { name: 'twitter:image', content: 'https://getclaimready.app/images/resources/spreadsheet-vs-pdf-inventory.png' }
    ],
    link: [
        { rel: 'canonical', href: 'https://getclaimready.app/resources/why-spreadsheet-isnt-enough-for-insurance-claims' }
    ]
})
</script>